import { useMemo } from 'react';

import { useUpdateCauseLedgerAccountCode } from '@givebacks/api/ledger';
import {
  useShowHistoricalTransactionCount,
  useUpdateAccountCodeForPos,
  useUpdateAccountCodeForProductFundraiser,
  useUpdateAccountCodeForShopToGive,
  useUpdateAccountCodeForStoreItem,
} from '@givebacks/api/payout';
import {
  storeKeys,
  useUpdateStoreCausePOSAccountCode,
  useUpdateStoreCausePOSDonationAccountCode,
  useUpdateStoreCauseProductFundraiserAccountCode,
  useUpdateStoreItemAccountCodes,
} from '@givebacks/api/store';
import { useUpdateFeeAccountCode } from '@givebacks/api/student';

import { ACCOUNT_CODE_FEATURES } from '../../../../utils';
import { AccountCodeFeatures } from './AssignAccountCodes.types';

const invalidationKey = ({
  feature,
  causeId,
  storeItemId,
}: {
  feature: AccountCodeFeatures;
  causeId: string;
  storeItemId?: string;
}) => {
  switch (feature) {
    case ACCOUNT_CODE_FEATURES.QUICK_PAY:
    case ACCOUNT_CODE_FEATURES.PRODUCT_FUNDRAISERS:
    case ACCOUNT_CODE_FEATURES.POS_DONATION:
      //from store/src/api/new/causes/get.ts
      return storeKeys.id(causeId).causes.id(causeId).detail;
    case ACCOUNT_CODE_FEATURES.SHOP_TO_GIVE:
      // from core/src/api/ledger/owner/get.ts
      return ['ledger', 'cause', causeId];
    case ACCOUNT_CODE_FEATURES.DONATION:
      // from store/src/api/CrowdFunding/Management/donations/donations.ts
      return [causeId, 'crowdfunding', 'donations'];
    case ACCOUNT_CODE_FEATURES.CAMPAIGN:
      // from store/src/api/new/campaigns/get.ts
      return storeItemId ? storeKeys.id(causeId).items.fundraisers.all : [];
    case ACCOUNT_CODE_FEATURES.MEMBERSHIP:
      // new table with RQ not yet implemented
      return causeId ? storeKeys.id(causeId).items.all : [];
    case ACCOUNT_CODE_FEATURES.PRODUCT:
      // from store/src/api/new/items/get.ts
      return causeId ? storeKeys.id(causeId).items.all : [];
    default:
      return [];
  }
};

const useAssignAccountCodes = ({ feature }: { feature: AccountCodeFeatures }) => {
  const { mutate: updateStoreItem, isPending: isStoreItemLoading } =
    useUpdateStoreItemAccountCodes();
  const { mutate: updatePOS, isPending: isPOSLoading } = useUpdateStoreCausePOSAccountCode();
  const { mutate: updatePOSDonation, isPending: isPOSDonationLoading } =
    useUpdateStoreCausePOSDonationAccountCode();
  const { mutate: updateProductFundraiser, isPending: isPFLoading } =
    useUpdateStoreCauseProductFundraiserAccountCode();
  const { mutate: updateLedgerCause, isPending: isLedgerLoading } =
    useUpdateCauseLedgerAccountCode();
  const { mutate: updateFeeAccountCode, isPending: isFeeLoading } = useUpdateFeeAccountCode();

  const update = useMemo<
    (
      requestParams: { accountCode?: string | null; causeId: string; storeItemId?: string },
      options
    ) => void
  >(() => {
    switch (feature) {
      case ACCOUNT_CODE_FEATURES.SHOP_TO_GIVE:
        return updateLedgerCause;
      case ACCOUNT_CODE_FEATURES.QUICK_PAY:
        return updatePOS;
      case ACCOUNT_CODE_FEATURES.PRODUCT_FUNDRAISERS:
        return updateProductFundraiser;
      case ACCOUNT_CODE_FEATURES.POS_DONATION:
        return updatePOSDonation;
      case ACCOUNT_CODE_FEATURES.FEES:
        return updateFeeAccountCode;
      default:
        return updateStoreItem;
    }
  }, [
    updatePOS,
    updateFeeAccountCode,
    updateProductFundraiser,
    updateLedgerCause,
    updateStoreItem,
    updatePOSDonation,
    feature,
  ]);

  const isLoading = useMemo(
    () =>
      isStoreItemLoading ||
      isPOSLoading ||
      isPFLoading ||
      isLedgerLoading ||
      isPOSDonationLoading ||
      isFeeLoading,
    [
      isStoreItemLoading,
      isPOSLoading,
      isPFLoading,
      isLedgerLoading,
      isPOSDonationLoading,
      isFeeLoading,
    ]
  );

  return {
    mutate: update,
    isLoading: isLoading,
  };
};

const useUpdateHistoricalTransactions = ({ feature }: { feature: AccountCodeFeatures }) => {
  const { mutate: updateTransactions, isPending: isStoreItemLoading } =
    useUpdateAccountCodeForStoreItem();
  const { mutate: updatePFTransactions, isPending: isPFTLoading } =
    useUpdateAccountCodeForProductFundraiser();
  const { mutate: updateSTGTransactions, isPending: isSTGTLoading } =
    useUpdateAccountCodeForShopToGive();
  const { mutate: updatePOSTransactions, isPending: isPOSTLoading } = useUpdateAccountCodeForPos();

  const update = useMemo<
    (
      requestParams: { accountCodeId?: string | null; causeId: string; storeItemId?: string },
      options
    ) => void
  >(() => {
    switch (feature) {
      case ACCOUNT_CODE_FEATURES.SHOP_TO_GIVE:
        return updateSTGTransactions;
      case ACCOUNT_CODE_FEATURES.QUICK_PAY:
        return updatePOSTransactions;
      case ACCOUNT_CODE_FEATURES.PRODUCT_FUNDRAISERS:
        return updatePFTransactions;
      default:
        return updateTransactions;
    }
  }, [
    updateTransactions,
    updatePFTransactions,
    updateSTGTransactions,
    updatePOSTransactions,
    feature,
  ]);

  const isLoading = useMemo(
    () => isStoreItemLoading || isPFTLoading || isSTGTLoading || isPOSTLoading,
    [isStoreItemLoading, isPFTLoading, isSTGTLoading, isPOSTLoading]
  );

  return { mutate: update, isLoading };
};

const useGetHistoricalTransactionCount = ({
  feature,
  causeId,
  storeItemId,
}: {
  feature: AccountCodeFeatures;
  causeId: string;
  storeItemId?: string;
}) => {
  const storeItemRequestId = useMemo(() => {
    switch (feature) {
      case ACCOUNT_CODE_FEATURES.SHOP_TO_GIVE:
        return 'shop_to_give';
      case ACCOUNT_CODE_FEATURES.QUICK_PAY:
        return 'pos';
      // TODO: update to sync with payout updates for cause pos_donation_account_code
      case ACCOUNT_CODE_FEATURES.POS_DONATION:
        return 'pos_donation';
      case ACCOUNT_CODE_FEATURES.PRODUCT_FUNDRAISERS:
        return 'product_fundraisers';
      default:
        if (!storeItemId) {
          throw new Error('Store Item ID required');
        }
        return storeItemId;
    }
  }, [feature, storeItemId]);

  const { data, isLoading: isLoading } = useShowHistoricalTransactionCount({
    causeId,
    storeItemId: storeItemRequestId,
  });

  return { data, isLoading };
};

export {
  invalidationKey,
  useAssignAccountCodes,
  useGetHistoricalTransactionCount,
  useUpdateHistoricalTransactions,
};
